import React, { useState } from 'react';
import { XMarkIcon, ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const MEMBER_TYPES = [
  { value: 'AUTO_SHOP', label: 'Auto Shop' },
  { value: 'ITP_STATION', label: 'ITP Station' },
];

const BulkImportModal = ({ show, onImport, onClose }) => {
  const [file, setFile] = useState(null);
  const [memberType, setMemberType] = useState('AUTO_SHOP');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      alert('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const importResult = await onImport(file, memberType);
      setResult(importResult);
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setMemberType('AUTO_SHOP');
    setResult(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Bulk Import Audience Members
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format</h4>
            <p className="text-sm text-blue-700 mb-2">
              Your CSV file should have the following columns:
            </p>
            <code className="text-xs bg-blue-100 px-2 py-1 rounded block">
              name,email,phone,cui
            </code>
            <p className="text-xs text-blue-600 mt-2">
              First row should be the header. Only name and email are required.
              Fields with commas should be enclosed in double quotes.
              Additional columns are ignored.
            </p>
          </div>

          {/* Member Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Member Type
            </label>
            <select
              value={memberType}
              onChange={(e) => setMemberType(e.target.value)}
              className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            >
              {MEMBER_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              All imported members will be assigned this type.
            </p>
          </div>

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <DocumentTextIcon className="h-8 w-8 text-green-500" />
                    <span className="text-sm text-gray-700">{file.name}</span>
                  </div>
                ) : (
                  <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                )}
                <div className="flex text-sm text-gray-600 justify-center">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500">
                    <span>{file ? 'Change file' : 'Upload a file'}</span>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">CSV up to 10MB</p>
              </div>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-md ${result.failedCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
              <h4 className={`text-sm font-medium ${result.failedCount > 0 ? 'text-yellow-800' : 'text-green-800'} mb-2`}>
                Import Complete
              </h4>
              <div className="text-sm space-y-1">
                <p className="text-gray-700">
                  <span className="font-medium">Total rows:</span> {result.totalRows}
                </p>
                <p className="text-green-700">
                  <span className="font-medium">Successful:</span> {result.successCount}
                </p>
                {result.failedCount > 0 && (
                  <p className="text-red-700">
                    <span className="font-medium">Failed:</span> {result.failedCount}
                  </p>
                )}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Errors ({result.errors.length}):</p>
                  <div className="max-h-48 overflow-y-auto bg-white rounded p-2 border">
                    {result.errors.map((error, idx) => (
                      <p key={idx} className="text-xs text-red-600 py-0.5">{error}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleImport}
              disabled={!file || importing}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;

